import { FilterSelectionState } from "@/filters/selection/FilterSelectionState";
import { Degree } from "@/models/Degree";
import { SortingState } from "@/sorting/SortingState";
import { ISeoMetaDataInfo } from "../../ISeoMetaDataInfo";
import { SeoMetaDataHandler } from "../../SeoMetaDataHandler";
import { FilterKey } from "@studyportals/search-filters";
import { SortingOptions } from "@/sorting/SortingOptions";
import { SearchOrganisationPresenter } from "@/presenters/SearchOrganisationPresenter";
import { FilterOptions } from "@/filters/options/FilterOptions";

export class University extends SeoMetaDataHandler {

	private readonly organisationPresenter: SearchOrganisationPresenter;

	public constructor(filterOptions: FilterOptions, organisationPresenter: SearchOrganisationPresenter) {
		super(filterOptions);

		this.organisationPresenter = organisationPresenter;
	}

	public async canHandle(sortState: SortingState, filterSelectionState: FilterSelectionState): Promise<boolean> {
		const metaRankingSortSelected = await this.sortingOptionSelected(SortingOptions.UNIVERSITY_META_RANKING, sortState);
		const singleUniversitySelected = await this.singleSelectionFor(FilterKey.ORGANISATIONS, filterSelectionState);

		return metaRankingSortSelected && singleUniversitySelected;
	}

	protected async selfHandle(degree: Degree, filterSelectionState: FilterSelectionState): Promise<ISeoMetaDataInfo> {
		const idOfOrganisation = this.getFilterOptionValueBy(FilterKey.ORGANISATIONS, filterSelectionState);
		const year = this.getCurrentYearForSeo();

		if (idOfOrganisation === null) {
			return this.createSeoInfoDefault(year);
		}

		return await this.createSeoInfoForOrganisation(idOfOrganisation, year);
	}

	private async createSeoInfoForOrganisation(idOfOrganisation: string, year: number): Promise<ISeoMetaDataInfo> {
		const university = await this.organisationPresenter.retrieve(idOfOrganisation);
		if (university === null) {
			return this.createSeoInfoDefault(year);
		}

		return this.createSeoInfo(university.name, year);
	}

	private createSeoInfoDefault(year: number): ISeoMetaDataInfo {
		const seoInfo: ISeoMetaDataInfo = {
			title: `Explore University Ranking ${year} | SP Meta Ranking`,

			description:
				`Discover the best universities in the world with Studyportals University Meta Rankings. ` +
				`Our algorithm summarises other university ranking sites to give you the best overview.`,
		};

		return seoInfo;
	}

	private async createSeoInfo(university: string, year: number): Promise<ISeoMetaDataInfo> {
		const seoInfo = {
			title: `${university} Ranking ${year} | SP Meta Ranking`,

			description:
				`Discover the best universities in the world with Studyportals University Meta Rankings. ` +
				`Our algorithm summarises other university ranking sites to give you the best overview.`
		};

		return Promise.resolve(seoInfo);
	}
}
